import { apiClient } from '../client';
import type { ApiResponse } from '../types';
import { unwrapResponse } from '../types';
import type {
  NotificationLog,
  InAppNotification,
  PaginatedResponse,
} from '@coi-live/shared-types';

interface SendNotificationInput {
  templateId: string;
  recipientEmails: string[];
  templateVars: Record<string, unknown>;
  relatedEntityType?: string;
  relatedEntityId?: string;
  /** Bypass the per-job recipient allowlist for ad-hoc one-off compose. */
  manualSend?: boolean;
  /** When true, server appends every active Risk Management dept member email. */
  ccRiskManagementDepartment?: boolean;
  /** Explicit additional CC emails appended server-side. */
  ccEmails?: string[];
}

interface ListNotificationsParams {
  limit?: number;
  cursor?: string;
  relatedEntityId?: string;
}

interface ListInAppParams {
  limit?: number;
  cursor?: string;
  unreadOnly?: boolean;
}

function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();

  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.cursor) searchParams.set('cursor', String(params.cursor));
  if (params.unreadOnly) searchParams.set('unreadOnly', 'true');
  if (params.relatedEntityId) searchParams.set('relatedEntityId', String(params.relatedEntityId));

  const qs = searchParams.toString();
  return qs ? `?${qs}` : '';
}

export const notificationsService = {
  async list(params: ListNotificationsParams = {}): Promise<PaginatedResponse<NotificationLog>> {
    const url = `/notifications${buildQueryString(params as Record<string, unknown>)}`;
    const { data } = await apiClient.get<ApiResponse<PaginatedResponse<NotificationLog>>>(url);
    return unwrapResponse(data);
  },

  async get(id: string): Promise<NotificationLog> {
    const { data } = await apiClient.get<ApiResponse<NotificationLog>>(`/notifications/${id}`);
    return unwrapResponse(data);
  },

  async send(input: SendNotificationInput): Promise<{ sent: number }> {
    const { data } = await apiClient.post<ApiResponse<{ sent: number }>>(
      '/notifications/send',
      input
    );
    return unwrapResponse(data);
  },

  async resend(id: string): Promise<{ resent: boolean }> {
    const { data } = await apiClient.post<ApiResponse<{ resent: boolean }>>(
      `/notifications/${id}/resend`,
      {}
    );
    return unwrapResponse(data);
  },

  async listInApp(params: ListInAppParams = {}): Promise<PaginatedResponse<InAppNotification>> {
    const url = `/notifications/in-app${buildQueryString(params as Record<string, unknown>)}`;
    const { data } =
      await apiClient.get<ApiResponse<PaginatedResponse<InAppNotification>>>(url);
    return unwrapResponse(data);
  },

  async getUnreadCount(): Promise<{ count: number }> {
    const { data } = await apiClient.get<ApiResponse<{ count: number }>>(
      '/notifications/in-app/unread-count'
    );
    return unwrapResponse(data);
  },

  async markAsRead(input: { notificationId?: string; all?: boolean }): Promise<unknown> {
    const { data } = await apiClient.post<ApiResponse<unknown>>(
      '/notifications/in-app/mark-read',
      input
    );
    return unwrapResponse(data);
  },
};
